import { forwardRef, useMemo, type ElementType } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageSquare, UserX, Loader2 } from "lucide-react";
import type { ChatMessage } from "@/integrations/backend/chat";
import { cn } from "@/lib/utils";

type ChatMessageListProps = {
  messages: ChatMessage[];
  loading?: boolean;
  trainerResolved?: boolean;
  trainerId?: string | null;
};

function formatTime(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

function dateKey(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toDateString();
}

type RenderItem =
  | { type: "date"; label: string; key: string }
  | { type: "message"; message: ChatMessage; showAvatar: boolean; key: string };

function buildRenderItems(messages: ChatMessage[]): RenderItem[] {
  const items: RenderItem[] = [];
  let lastDate = "";
  let lastSenderId: string | null = null;

  for (const m of messages) {
    const dk = dateKey(m.created_at);
    if (dk && dk !== lastDate) {
      items.push({ type: "date", label: formatDateLabel(m.created_at), key: `date-${dk}` });
      lastDate = dk;
      lastSenderId = null;
    }

    const showAvatar = m.sender_id !== lastSenderId;
    items.push({ type: "message", message: m, showAvatar, key: m.id });
    lastSenderId = m.sender_id;
  }

  return items;
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-dark-slate border border-primary/15 flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-primary/70" />
      </div>
      <p className="text-white font-medium mb-1">{title}</p>
      <p className="text-sm text-gray-light max-w-xs">{description}</p>
    </div>
  );
}

export const ChatMessageList = forwardRef<HTMLDivElement, ChatMessageListProps>(
  function ChatMessageList({ messages, loading, trainerResolved, trainerId }, ref) {
    const renderItems = useMemo(() => buildRenderItems(messages), [messages]);

    if (!trainerResolved) {
      return (
        <div ref={ref} className="flex-1 overflow-y-auto min-h-0 bg-dark-card">
          <EmptyState
            icon={Loader2}
            title="Opening chat…"
            description="Getting your conversation ready."
          />
        </div>
      );
    }

    if (!trainerId) {
      return (
        <div ref={ref} className="flex-1 overflow-y-auto min-h-0 bg-dark-card">
          <EmptyState
            icon={UserX}
            title="No trainer assigned"
            description="Please contact support to get assigned a trainer."
          />
        </div>
      );
    }

    if (loading) {
      return (
        <div ref={ref} className="flex-1 overflow-y-auto min-h-0 bg-dark-card">
          <EmptyState
            icon={Loader2}
            title="Loading messages…"
            description="Fetching your conversation history."
          />
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div ref={ref} className="flex-1 overflow-y-auto min-h-0 bg-dark-card">
          <EmptyState
            icon={MessageSquare}
            title="Start the conversation"
            description="Say hello to your trainer — they're here to help you reach your goals."
          />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="flex-1 overflow-y-auto min-h-0 bg-dark-card p-4 sm:p-5 space-y-1"
      >
        {renderItems.map((item) => {
          if (item.type === "date") {
            return (
              <div key={item.key} className="flex justify-center py-3">
                <span className="text-xs font-medium text-gray-light bg-dark-slate/80 border border-primary/10 rounded-full px-3 py-1">
                  {item.label}
                </span>
              </div>
            );
          }

          const m = item.message;
          const isSender = m.is_sender;

          return (
            <div
              key={item.key}
              className={cn(
                "flex items-end gap-2",
                isSender ? "justify-end" : "justify-start",
                !item.showAvatar && (isSender ? "pr-10" : "pl-10")
              )}
            >
              {!isSender && item.showAvatar && (
                <Avatar className="w-8 h-8 flex-shrink-0">
                  {m.sender_avatar_url ? (
                    <AvatarImage src={m.sender_avatar_url} alt={m.sender_name} />
                  ) : (
                    <AvatarFallback className="bg-conditioning text-white text-xs">
                      {m.sender_initials}
                    </AvatarFallback>
                  )}
                </Avatar>
              )}
              {!isSender && !item.showAvatar && <div className="w-8 flex-shrink-0" />}

              <div
                className={cn(
                  "max-w-[min(75%,32rem)] rounded-2xl px-4 py-2.5 text-base shadow-sm",
                  isSender
                    ? "bg-primary text-black rounded-br-md"
                    : "bg-dark-slate border border-primary/10 text-white rounded-bl-md"
                )}
              >
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                <div
                  className={cn(
                    "text-xs mt-1",
                    isSender ? "text-black/60" : "text-gray-light"
                  )}
                >
                  {formatTime(m.created_at)}
                </div>
              </div>

              {isSender && item.showAvatar && (
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">
                    {m.sender_initials}
                  </AvatarFallback>
                </Avatar>
              )}
              {isSender && !item.showAvatar && <div className="w-8 flex-shrink-0" />}
            </div>
          );
        })}
      </div>
    );
  }
);
